import React, { useEffect, useRef, useState } from 'react';

export interface ContextMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  danger?: boolean;
  separator?: boolean;
  subMenu?: ContextMenuItem[];
}

interface ContextMenuProps {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
}

const ContextMenuItemRender: React.FC<{ 
  item: ContextMenuItem; 
  onClose: () => void;
  depth?: number;
}> = ({ item, onClose, depth = 0 }) => {
  const [showSubMenu, setShowSubMenu] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (hoverTimeout) clearTimeout(hoverTimeout);
    if (!item.disabled && item.subMenu) {
      setShowSubMenu(true);
    }
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setShowSubMenu(false);
    }, 200); // Small delay to allow moving mouse to submenu
    setHoverTimeout(timeout);
  };

  if (item.separator) {
    return <div className="my-1 border-t border-gray-700" />;
  }

  return (
    <div 
      className="relative w-full"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        onClick={(e) => {
            e.stopPropagation();
            if (!item.disabled) {
                if (!item.subMenu) {
                    item.onClick?.();
                    onClose();
                }
            }
        }}
        disabled={item.disabled}
        className={`
          w-full px-4 py-2 text-left text-sm flex items-center justify-between space-x-3
          ${item.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-700 cursor-pointer'}
          ${item.danger ? 'text-red-400' : 'text-gray-300'}
        `}
      >
        <div className="flex items-center space-x-3">
            {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
            <span>{item.label}</span>
        </div>
        {item.subMenu && (
            <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
        )}
      </button>

      {showSubMenu && item.subMenu && (
        <div 
            className="absolute left-full top-0 bg-gray-800 border border-gray-700 rounded-lg shadow-xl py-2 z-[60] min-w-[200px]"
            style={{ marginLeft: '4px', marginTop: '-4px' }}
        >
          {item.subMenu.map((subItem, idx) => (
            <ContextMenuItemRender 
                key={idx} 
                item={subItem} 
                onClose={onClose} 
                depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ContextMenu: React.FC<ContextMenuProps> = ({ x, y, items, onClose }) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is inside the menu or any of its submenus (which are children in DOM)
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Prevent menu from going off-screen (basic check)
  // In a real app we'd measure window size and adjust
  const style: React.CSSProperties = {
      top: `${y}px`,
      left: `${x}px`
  };

  return (
    <div
      ref={menuRef}
      className="fixed bg-gray-800 border border-gray-700 rounded-lg shadow-xl py-2 z-50 min-w-[200px]"
      style={style}
    >
      {items.map((item, index) => (
        <ContextMenuItemRender key={index} item={item} onClose={onClose} />
      ))}
    </div>
  );
};

export default ContextMenu;
